import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
} from '@mui/material';
import axios from 'axios';
import { QueryParameterNames } from './ApiAuthorizationConstants';

interface PasswordErrors {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const ChangeCredentials = () => {
    const [currentUser, setCurrentUser] = useState<string>('');
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState<PasswordErrors>({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [returnUrl, setReturnUrl] = useState<string | null>(null);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const response = await axios.get('/api/auth/user');
                const data = await response.data;
                setCurrentUser(data.email);
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };

        fetchCurrentUser();
        let params = new URLSearchParams(window.location.search);
        let fromQuery = params.get(QueryParameterNames.ReturnUrl);
        setReturnUrl(fromQuery);
    }, []);

    const validateOldPassword = (password: string): string => {
        if (!password) return 'Password is required';
        return '';
    };

    const validatePassword = (password: string): string => {
        if (!password) return 'Password is required';
        if (password.length < 6) return 'Password must be at least 6 characters';
        if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
        if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
        if (!/[!@#$%^&*(),.?":{}|<>-]/.test(password)) return 'Password must contain at least one special character';
        return '';
    };

    const handleOldPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setOldPassword(value);
        setErrors(prev => ({
            ...prev,
            oldPassword: validateOldPassword(value),
        }));
    };

    const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewPassword(value);
        setErrors(prev => ({
            ...prev,
            newPassword: validatePassword(value),
        }));
    };

    const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setConfirmPassword(value);
        setErrors(prev => ({
            ...prev,
            confirmPassword: value !== newPassword ? 'Passwords must match' : '',
        }));
    };

    const [success, setSuccess] = useState(false);

    const handleSave = async () => {
        const oldPasswordError = validateOldPassword(oldPassword);
        const newPasswordError = validatePassword(newPassword);
        const confirmPasswordError = newPassword !== confirmPassword ? 'Passwords must match' : '';

        if (newPasswordError || confirmPasswordError) {
            setErrors({
                oldPassword: oldPasswordError,
                newPassword: newPasswordError,
                confirmPassword: confirmPasswordError,
            });
            return;
        }

        try {
            const response = await axios.post('/api/auth/changeCredentials', { oldPassword, newPassword });

            if (response.status === 200) {
                setSuccess(true);
                // set a timer for 3 seconds to close the dialog
                setTimeout(() => closeAndExit(), 3000);
            } else {
                if (response.data) {
                    let message = response.data.message;
                    let errors = response.data.errors;

                    if (errors) {
                        message += ': ' + Object.values(errors).join(', ');
                    }

                    setErrors(prev => ({
                        ...prev,
                        newPassword: message,
                        confirmPassword: ''
                    }));
                } else
                    setErrors(prev => ({
                        ...prev,
                        newPassword: `Failed to change password. Server returned status ${response.status}`,
                        confirmPassword: ''
                    }));
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                let errorMessage = error.response?.data?.message || 'Failed to change password. Please try again.';
                let errors = error.response?.data?.errors;

                const isIncorrectPassword = Object.values(error.response?.data?.errors ?? {}).some((value) => {
                    const msg = value as string;
                    return msg.toLowerCase().includes("incorrect password");
                });

                if (errors) {
                    errorMessage ? errorMessage += ': ' + Object.values(errors).join(', ')
                        : Object.values(errors).join(', ');
                }

                if (isIncorrectPassword)
                    setErrors(prev => ({
                        ...prev,
                        oldPassword: errorMessage
                    }));
                else
                    setErrors(prev => ({
                        ...prev,
                        newPassword: errorMessage,
                        confirmPassword: ''
                    }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    newPassword: 'An unexpected error occurred while changing password',
                    confirmPassword: ''
                }));
            }

            console.error('Error changing password:', error);
        }
    };

    const isValid = !errors.newPassword && !errors.confirmPassword && newPassword && confirmPassword;

    function closeAndExit() {
        window.location.href = returnUrl ?? '/';
    }
    function handleCancel(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
        event.preventDefault();
        closeAndExit();
    }

    return (
        <Dialog
            open={true}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
            }}
            disableEscapeKeyDown={false}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Change Password</DialogTitle>

            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label="Current User"
                        value={currentUser}
                        disabled
                        margin="normal"
                    />
                    <TextField
                        autoFocus
                        fullWidth
                        label="Old Password"
                        type="password"
                        value={oldPassword}
                        onChange={handleOldPasswordChange}
                        error={!!errors.oldPassword}
                        helperText={errors.oldPassword}
                        margin="normal"
                        required
						variant='standard'
						InputLabelProps={{
							shrink: true,
							style: {
								color: "black",
							}
						}}
		/>
                    <TextField
                        fullWidth
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={handleNewPasswordChange}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword}
                        margin="normal"
                        required
						variant='standard'
						InputLabelProps={{
							shrink: true,
							style: {
								color: "black",
							}
						}}
		/>
                    <TextField
                        fullWidth
                        label="Confirm New Password"
                        type="password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        margin="normal"
                        required
						variant='standard'
						InputLabelProps={{
							shrink: true,
							style: {
								color: "black",
							}
						}}
		/>
                    {success &&
                        <Alert
                            severity="success"
                            sx={{ mt: 2, mb: 2 }}
                            style={{ backgroundColor: "green", color: "white" }}
                        >
                            Password changed successfully!
                        </Alert>
                    }
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleCancel} color="primary">
                    Cancel
                </Button>

                <Button onClick={handleSave} color="primary" disabled={!isValid}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeCredentials;

